import { makeAutoObservable, runInAction } from 'mobx'
import { debounce, compact } from 'lodash'
import { isCancel } from 'axios'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { uiStore } from 'shared/store/uiStore'
import {
  type Conversation,
  ConversationsApi,
  type IParamsConversationsAllForContact,
  type IResponseConversation,
  type IResponseConversationsAllForContact,
} from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { conversationTemplate } from 'entities/Conversation/templates/conversationTemplate'
import type { IResponseContact } from 'entities/Contacts/api/types'
import type { IResponseUser } from 'entities/Users/api/types'
import type { IResponseNumber } from 'entities/Phone/api/types'
import { FiltersStore } from 'features/DropdownFilter'
import { OrdersStore } from 'features/DropdownOrder'
import { type ProfilePageStore } from 'pages/conversations/pages/search/pages/profile/store/profilePageStore'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'

export class SectionConversationStore {
  private _itemsMap: Map<number, Conversation> = new Map()
  private _itemsOrder: number[] = []
  private _currentItemId: number | null = null
  private _visibleUI = false
  readonly filtersStore: FiltersStore
  readonly ordersStore: OrdersStore
  private _loading = false
  private _mainLoading = true
  private _initialized = false
  private _openCount = 0
  private _cancelRequest = new RequestCancelAxios()
  readonly debounceLoadConversationList: ReturnType<typeof debounce>

  constructor(private _profilePageStore: ProfilePageStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this.debounceLoadConversationList = debounce(this._loadConversationList, 500)
    this.filtersStore = new FiltersStore(this._entities, {
      enableCount: true,
      showFilterByKeys: ['open', 'closed', 'outbound'],
      onChange: () => {
        if (this._mainLoading) return

        this.fetchConversationList()
      },
    })
    this.ordersStore = new OrdersStore({
      onChange: () => {
        if (this._mainLoading) return

        this.fetchConversationList()
      },
    })
  }

  get contact() {
    return this._profilePageStore.contact
  }

  get params(): IParamsConversationsAllForContact {
    return {
      limit: this._limit,
      filter: this.filtersStore.filter.key,
      order: this.ordersStore.order.key,
      grouped_response: true,
      offset: 0,
    }
  }

  get notFoundText() {
    if (this.filtersStore.filter.key === 'closed') {
      return 'No closed conversations'
    }

    if (this.filtersStore.filter.key === 'outbound') {
      return 'No outbound conversations'
    }

    return 'No conversations'
  }

  private get _limit() {
    return this._profilePageStore.limit || 20
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setVisibleUI = (value: boolean) => {
    this._visibleUI = value
  }

  init = async () => {
    runInAction(() => {
      this._mainLoading = true
    })

    await this.filtersStore.loadFilters()

    const conversation = this._profilePageStore.conversation

    if (conversation) {
      if (['open', 'outbound', 'closed'].includes(conversation.filterKey)) {
        this.filtersStore.setFilter(conversation.filterKey)
      } else {
        this.filtersStore.setFilter('open')
      }
    } else if (this.hideFilter) {
      this.filtersStore.setFilter('open')
    }

    await this._loadConversationList()

    runInAction(() => {
      this._mainLoading = false
      this._initialized = true
    })
  }

  dispose = () => {
    this.debounceLoadConversationList?.cancel?.()
    this._cancelRequest.reset()
    this._itemsOrder = []
    this._itemsMap.clear()
    this._currentItemId = null
    this.filtersStore.reset()
    this.ordersStore.reset()
    this._loading = false
    this._mainLoading = true
    this._initialized = false
    this._openCount = 0
    this._entities.conversationStore.reset()
  }

  private _checkConversationOpenFilter = async () => {
    try {
      const params: IParamsConversationsAllForContact = {
        ...this.params,
        filter: 'open',
      }

      const response = await ConversationsApi.getConversationsAllForContact(
        this.contact.id,
        params,
        {
          cancelToken: this._cancelRequest.token,
        }
      )
      const data = response.data

      this._openCount = data.conversations.length

      if (this._openCount) {
        this._profilePageStore.setHideFilter(false)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  fetchConversationList = () => {
    this.setLoading(true)
    this.resetConversation()
    this.debounceLoadConversationList()
  }

  private _loadConversationList = async () => {
    try {
      this.setLoading(true)

      this.resetConversation()
      this._cancelRequest.init()

      const [response] = await Promise.all([
        ConversationsApi.getConversationsAllForContact(this.contact.id, this.params, {
          cancelToken: this._cancelRequest.token,
        }),
        this._checkConversationOpenFilter(),
      ])

      const data = response.data

      this.applyConversations(data)

      this.setLoading(false)
    } catch (e) {
      logger.error(e)

      if (!isCancel(e)) {
        this.setLoading(false)
      }
    }
  }

  resetConversation = () => {
    this._itemsOrder = []
    this._itemsMap.clear()
    this.filtersStore.setCount(0)
  }

  applyConversations = (response: IResponseConversationsAllForContact) => {
    const contacts = compact<IResponseContact>(Object.values(response.contacts))
    const owners = compact<IResponseUser>(Object.values(response.owners))
    const numbers = compact<IResponseNumber>(Object.values(response.numbers))

    this._entities.usersStore.addItems(owners)
    this._entities.contactsStore.addItems(contacts)
    this._entities.numbersStore.addItems(numbers)
    this._entities.conversationStore.addItems(response.conversations)

    response.conversations.forEach((item) => {
      this.addConversation(item)
    })

    if (response.conversations.length) {
      if (this._initialized) {
        this.setCurrentItemId(response.conversations[0].id)
      } else {
        if (this.isNewConversation) return
        if (!this._profilePageStore.conversation) {
          this.setCurrentItemId(response.conversations[0].id)
        }
      }
    }
  }

  addConversation = (item: IResponseConversation, top?: boolean) => {
    const conversation = this._entities.conversationStore.getItem(item.id)

    if (conversation) {
      this._itemsMap.set(item.id, conversation)

      const isDuplicate = this._itemsOrder.some((id) => id === item.id)

      if (!isDuplicate) {
        if (top) {
          this._itemsOrder.unshift(item.id)
        } else {
          this._itemsOrder.push(item.id)
        }
      }

      this.filtersStore.setCount(this._itemsMap.size)
    }

    this._entities.contactsStore.addItems(item.participants)
  }

  setCurrentItemId = (id: number, is_delete_message?: boolean) => {
    if (id === 0) {
      this._setNewConversation()
    }

    this._currentItemId = id

    if (this._visibleUI) {
      const query = new URLSearchParams(window.location.search)

      if (is_delete_message) {
        query.delete('messageId')
      }

      uiStore.changeRoute({
        path: `/${ConversationsPath.searchProfile}/${this._currentItemId}`,
        query: query.toString(),
      })
    }
  }

  private _setNewConversation = () => {
    const user_owner_id = this._entities.usersStore.user_owner_id
    const currentInboxId = this._entities.inboxesStore.currentInbox?.id

    const newConversation: IResponseConversation = {
      ...conversationTemplate,
      id: 0,
      contact_id: this.contact.id,
      inbox_id: currentInboxId || 0,
      user_id: user_owner_id,
      closed_at: '',
      is_group: false,
      is_locked: true,
      last_message_at: '',
      last_inbound_at: '',
      last_read_at: '',
      group_name: null,
      participants: [],
      number_id: 0,
    }

    this._entities.conversationStore.addItem(newConversation)
    this._entities.contactsStore.addItem(this.contact.origin)

    this.sectionMessagesStore.conversationMessagesStore.setConversationId(0, true)
  }

  onOpenConversation = (item: Conversation) => {
    this.setCurrentItemId(item.id, true)
  }

  onOpenNewConversation = () => {
    this.setCurrentItemId(0)
  }

  get conversation() {
    if (this._currentItemId === 0) {
      return this._entities.conversationStore.getItem(this._currentItemId)
    }

    if (!this._currentItemId) return

    return this._itemsMap.get(this._currentItemId)
  }

  get items() {
    return this._itemsOrder.reduce<Conversation[]>((state, id) => {
      const item = this._itemsMap.get(id)

      if (item) state.push(item)

      return state
    }, [])
  }

  get currentItemId() {
    return this._currentItemId
  }

  get isNewConversation() {
    return this._currentItemId === 0
  }

  get loading() {
    return this._loading || this._mainLoading
  }

  get openCount() {
    return this._openCount
  }

  get sectionMessagesStore() {
    return this._profilePageStore.sectionMessagesStore
  }

  get hideFilter() {
    return this._profilePageStore.hideFilter
  }
}
